import { IModel, IViolation, IAttribute, ModelConstants, ParameterValue, IValidationContext } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { AzureFunctionLinkedService } from 'app/model/linkedservice/azure-function.linkedservice';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { format } from 'app/shared/utils/string.util';
import { FabricUserDataFunctionsLinkedService } from '../linkedservice/fabric-userdatafunctions.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { Copier } from 'app/model/base/copier';

export enum FunctionRunType {
    FABRIC_FUNCTION = 'fabricFunction',
    AZURE_FUNCTION = 'azureFunction'
}

@Metamodel.model({
    name: ModelConstants.azureFunctionActivity,
    referable: true
})
export class AzureFunctionActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly BODY: IAttribute;
    public static readonly FUNCTIONNAME: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly FUNCTIONSETID: IAttribute;
    public static readonly WORKSPACEID: IAttribute;
    public static readonly PARAMETERS: IAttribute;
    public runType: string = FunctionRunType.AZURE_FUNCTION;

    @Metamodel.attribute({
        templatable: true,
        association: 'METHOD',
        constraint: {
            validator: AzureFunctionActivity.validateMethod
        }
    })
    public method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS' | 'HEAD' | 'TRACE';

    @Metamodel.attribute({
        templatable: true,
        association: 'HEADERS',
        constraint: {
            validator: AzureFunctionActivity.validateHeader
        }
    })
    public headers: ParameterConfig[];

    @Metamodel.attribute({
        templatable: true,
        association: 'BODY',
        constraint: {
            validator: AzureFunctionActivity.validateBody
        },
        accepts: ['String', 'Object']
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body: any;

    @Metamodel.attribute({
        templatable: true,
        association: 'PARAMETERS'
    })
    public parameters: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_FunctionName,
        constraint: {
            required: true
        },
        association: 'FUNCTIONNAME',
        templatable: true
    })
    public functionName?: string;

    @Metamodel.attribute({
        display: __IS_FABRIC_DPE__
            ? stringResources.AzureFunction_ConnectionLabel
            : format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureFunctionActivityLabel),
        constraint: {
            validator: AzureFunctionActivity.validateAzureFunctionConnection
        },
        reference: true,
        type: AzureFunctionLinkedService.MODEL
    })
    public linkedService?: AzureFunctionLinkedService;

    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        constraint: {
            validator: AzureFunctionActivity.validateUserDataFunctionConnection
        },
        reference: true,
        type: FabricUserDataFunctionsLinkedService.MODEL
    })
    public udfLinkedService?: FabricUserDataFunctionsLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.UserDataFunctionsLabel,
        association: 'FUNCTIONSETID',
        constraint: {
            validator: AzureFunctionActivity.validateFunctionSetId
        },
        templatable: true
    })
    public functionSetId?: string;

    @Metamodel.attribute({
        display: stringResources.Workspace,
        association: 'WORKSPACEID',
        constraint: {
            validator: AzureFunctionActivity.validateWorkspaceId
        },
        templatable: true
    })
    public workspaceId?: string;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validateBody(azureFunctionActivity?: AzureFunctionActivity, body?: any): IViolation[] | undefined {
        if (
            azureFunctionActivity &&
            (azureFunctionActivity.method === 'PUT' || azureFunctionActivity.method === 'POST')
        ) {
            if (!body) {
                return [
                    {
                        message: () => stringResources.Activity_Error_BodyRequired
                    }
                ];
            }
        }
        return undefined;
    }
    private static validateHeader(
        _azureFunctionActivity?: AzureFunctionActivity,
        headers?: ParameterConfig[],
        violation?: IViolation
    ): IViolation[] | undefined {
        if (!headers) {
            return undefined;
        }

        if (violation && violation.context) {
            // only validate in this violation's context
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headerIndex = (violation.context as any).index;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isKey = (violation.context as any).field === 'name';

            if (headerIndex < 0 || headerIndex >= headers.length) {
                // this could happen if a header row was deleted
                return undefined;
            }

            if (isKey) {
                return !!headers[headerIndex].name ? undefined : [violation];
            } else {
                return !!headers[headerIndex].value || !!ParameterConfig.VALUE.templateValue(headers[headerIndex])
                    ? undefined
                    : [violation];
            }
        } else {
            // check all the headers
            const violations = new Array<IViolation>();

            for (let i = 0; i < headers.length; ++i) {
                if (!headers[i].name) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderKey,
                        context: {
                            field: 'name',
                            index: i
                        }
                    });
                }

                if (!headers[i].value && !ParameterConfig.VALUE.templateValue(headers[i])) {
                    violations.push({
                        message: () => stringResources.ErrorInvalidHeaderValue,
                        context: {
                            field: 'value',
                            index: i
                        }
                    });
                }
            }

            return violations.length > 0 ? violations : undefined;
        }
    }

    private static validateWorkspaceId(_azureFunctionActivity?: AzureFunctionActivity, workspaceId?: string): IViolation[] | undefined {
        if (!workspaceId && _azureFunctionActivity.runType === FunctionRunType.FABRIC_FUNCTION) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.Workspace)
                }
            ];
        }
        return undefined;
    }

    private static validateFunctionSetId(_azureFunctionActivity?: AzureFunctionActivity, functionSetId?: string): IViolation[] | undefined {
        if (!functionSetId && __IS_FABRIC_DPE__ && _azureFunctionActivity.runType === FunctionRunType.FABRIC_FUNCTION) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.UserDataFunctionsLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateAzureFunctionConnection(_azureFunctionActivity?: AzureFunctionActivity, connection?: string): IViolation[] | undefined {
        if (!connection && _azureFunctionActivity.runType === FunctionRunType.AZURE_FUNCTION) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.ConnectionLabel)
                }
            ];
        }
        return undefined;
    }

    private static async validateUserDataFunctionConnection(_azureFunctionActivity?: AzureFunctionActivity, connection?: FabricUserDataFunctionsLinkedService, _violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const useTokenServiceInDFActivity = (
            await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService')
        ).UseTokenServiceInDFActivity;
        if (useTokenServiceInDFActivity && _azureFunctionActivity.runType === FunctionRunType.FABRIC_FUNCTION) {
            if (!connection) {
                return [
                    {
                        message: () => format(stringResources.RequiredTemplate, stringResources.ConnectionLabel)
                    }
                ];
            } else if (connection && connection.getLinkedServiceType() != Metamodel.modelFor(FabricUserDataFunctionsLinkedService).name) {
                return [
                    {
                        message: () =>
                            format(
                                stringResources.ActivityNotSupportLinkedServiceError,
                                format(stringResources.ModelName_ActivityTemplate, stringResources.AzureFunctionActivityLabel),
                                format(stringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(connection).name)
                            ),
                        messageType: 'unsupported'
                    }
                ];
            }
        }
        return undefined;
    }

    private static validateMethod(_azureFunctionActivity?: AzureFunctionActivity, method?: string): IViolation[] | undefined {
        if (!method && _azureFunctionActivity.runType === FunctionRunType.AZURE_FUNCTION) {
            return [
                {
                    message: () => format(stringResources.RequiredTemplate, stringResources.MethodLabel)
                }
            ];
        }
        return undefined;
    }

    public onActivityCloned(clonedActivity: AzureFunctionActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
                if (this.linkedService?.parameterized) {
                    clonedActivity.linkedService = Copier.clone(
                        context,
                        [this.linkedService],
                        undefined,
                        true
                    )[0] as AzureFunctionLinkedService;
                }
                if (this.udfLinkedService?.parameterized) {
                    clonedActivity.udfLinkedService = Copier.clone(
                        context,
                        [this.udfLinkedService],
                        undefined,
                        true
                    )[0] as FabricUserDataFunctionsLinkedService;
                }
            
        }
    }
}
